import React, { useContext, useState, useRef, Fragment } from "react";
import AppContext from "../../context/AppContext";
import Cookies from "js-cookie";
import { toast } from "../../utils/toast";
import { Dialog, Transition } from "@headlessui/react";
import { Spinner } from "../../Spinner";
import { Upload, Paperclip, CheckCircle2 } from "lucide-react";
import translations from "../common/Translations";
import CommonButton from "../common/CommonButton";
import ModalCloseButton from "../common/ModalCloseButton";
import useDirection from "../../hooks/useDirection";

const ALLOWED_EXCEL_EXTENSIONS = [".xlsx", ".xls"];

export const UploadExcelReport = ({ open, setOpen, fetchInventoryData }) => {
  const { theme, selectedOrgId, langMode, userType, projectId } = useContext(AppContext);
  const { isRTL: isRtl } = useDirection();
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const cancelButtonRef = useRef(null);

  const navigateToBack = () => {
    setOpen(false);
  };

  const handleFileChange = (event) => {
    const selected = event.target.files[0];
    if (!selected) return;
    const hasValidExtension = ALLOWED_EXCEL_EXTENSIONS.some((ext) =>
      selected.name.toLowerCase().endsWith(ext)
    );
    if (!hasValidExtension) {
      toast.error(translations[langMode]?.onlyexcelallowed ?? "Only .xlsx and .xls files are allowed.");
      event.target.value = "";
      setFile(null);
      return;
    }
    setFile(selected);
  };

  const UploadInventoryReport = async () => {
    const token = Cookies.get("accessToken");
    if (!file) {
      console.error(translations[langMode].nofileselc);
      return;
    }
    const formData = new FormData();
    formData.append("excel", file);
    formData.append('projectId',projectId)
    if (userType === "super_admin" && selectedOrgId) {
      formData.append("organizationId", selectedOrgId);

    }
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BASE_BACKEND_API_URL}api/v1/meter-inventory/excel-upload`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );
      if (response.ok) {
        toast.success(translations[langMode].fileuploadedsucc);
        setOpen(false);
        setLoading(false);
        await fetchInventoryData();
      } else {
        const errorData = await response.json().catch(() => null);
        toast.error(errorData?.body?.value || translations[langMode].failedtouploadfile);
        setLoading(false);
      }
    } catch (error) {
      console.error("Error uploading file:", error);
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[100]"
        initialFocus={cancelButtonRef}
        onClose={() => setOpen(false)}
        dir={isRtl ? "rtl" : "ltr"}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className={`fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity `}
          />
        </Transition.Child>
        <div className="fixed inset-0 z-[100] w-screen overflow-y-auto">
          <div className="flex min-h-full justify-center p-2.5 sm:p-4 text-center items-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                className={`relative transform overflow-hidden rounded-xl sm:rounded-2xl bg-white shadow-xl transition-all sm:my-8 w-full max-w-lg max-h-[88vh] flex flex-col ${
                  theme === "dark"
                    ? "dark-mode"
                    : theme === "high-contrast"
                    ? "high-contrast"
                    : ""
                }`}
              >
                {loading && <Spinner />}
                <div className="shrink-0 flex justify-between items-center px-3.5 sm:px-5 py-3 sm:py-4 border-b border-slate-100">
                  <h4
                    className={`text-sm sm:text-base font-semibold
          ${theme === "dark" ? "text-white" : "text-gray-800"}
          ${theme === "high-contrast" ? "text-black" : "text-gray-700"}`}
                  >
                    {translations[langMode].uploadtheinventorysheet}
                  </h4>
                  <ModalCloseButton onClick={navigateToBack} />
                </div>
                <div
                  className={`flex-1 min-h-0 overflow-y-auto bg-white p-3.5 sm:p-5 flex flex-col gap-1.5 ${
                    theme === "dark"
                      ? "dark-mode"
                      : theme === "high-contrast"
                      ? "high-contrast"
                      : ""
                  }`}
                >
                  <label
                    className={`text-xs sm:text-sm font-semibold mb-0.5 ${
                      theme === "dark" ? "text-white" : theme === "high-contrast" ? "text-black" : "text-gray-800"
                    }`}
                  >
                    {translations[langMode]?.uploadinventorysheetlabel ?? "Upload Inventory Sheet (XLSX/XLS)"}
                  </label>
                  <div
                    className={`relative w-full overflow-hidden rounded-lg border px-2.5 sm:px-3 py-2 sm:py-2.5 transition-colors focus-within:border-blue-500 focus-within:outline-none ${
                      theme === "dark"
                        ? "border-gray-600 bg-[#0f172a]"
                        : theme === "high-contrast"
                        ? "border-black bg-white"
                        : "border-gray-300 bg-white"
                    }`}
                  >
                    <input
                      type="file"
                      accept=".xlsx, .xls"
                      onChange={handleFileChange}
                      className="absolute inset-0 z-20 h-full w-full cursor-pointer opacity-0"
                    />
                    <div
                      className={`relative z-10 flex items-center justify-between gap-2 ${
                        ""
                      }`}
                    >
                      <div className="flex min-w-0 flex-1 items-center gap-2">
                        <span className="hidden sm:inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#236DB4]/10 text-[#236DB4]">
                          <Upload size={15} />
                        </span>
                        <span className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-[#236DB4] px-2.5 py-1.5 text-xs font-semibold text-white sm:px-3 sm:text-sm">
                          <Upload size={13} className="sm:hidden shrink-0" />
                          <span>{translations[langMode]?.chooseFile ?? "Choose File"}</span>
                        </span>
                        <span
                          className={`truncate text-xs sm:text-sm font-medium ${
                            file ? "text-[#236DB4] font-semibold" : theme === "dark" ? "text-gray-200" : "text-gray-500"
                          }`}
                        >
                          {file?.name || translations[langMode]?.noFile || "No file chosen"}
                        </span>
                      </div>
                      <div className={`shrink-0 text-gray-400 ${theme === "dark" ? "text-gray-300" : ""}`}>
                        <Paperclip size={16} />
                      </div>
                    </div>
                  </div>
                  <div className="mt-1 flex items-center justify-between px-0.5 text-xs text-gray-500">
                    <span className="inline-flex items-center gap-1 text-[11px] sm:text-xs">
                      <Upload size={11} /> XLSX, XLS
                    </span>
                    {file && (
                      <span className="inline-flex items-center gap-1 text-[11px] sm:text-xs font-medium text-green-600 truncate max-w-[140px]">
                        <CheckCircle2 size={11} className="shrink-0" />
                        {translations[langMode]?.selected ?? "Selected"}
                      </span>
                    )}
                  </div>
                </div>
                <div
                  className={`shrink-0 sticky bottom-0 bg-white flex w-full justify-end items-center gap-2 sm:gap-3 border-t px-3.5 sm:px-5 py-3 sm:py-4 ${
                    theme === "dark"
                      ? "dark-mode"
                      : theme === "high-contrast"
                      ? "high-contrast"
                      : ""
                  }`}
                >
                  <CommonButton colorIntent="danger"
                    onClick={navigateToBack}
                    theme={theme}
                    variant="danger"
                    wrapperClassName="flex-1 min-w-0 flex sm:inline-block sm:flex-initial"
                    className="w-full !h-10 !rounded-lg justify-center"
                  >
                    {translations[langMode].cancle}
                  </CommonButton>
                  <CommonButton colorIntent="success"
                    onClick={UploadInventoryReport}
                    theme={theme}
                    variant="primary"
                    wrapperClassName="flex-1 min-w-0 flex sm:inline-block sm:flex-initial"
                    className="w-full !h-10 !rounded-lg justify-center"
                  >
                    {translations[langMode].upload}
                  </CommonButton>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
